import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { dramas } from "@/mocks/dramas";
import { reviews } from "@/mocks/reviews";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import ReviewCard from "@/components/base/ReviewCard";
import EmptyState from "@/components/feature/EmptyState";

const PAGE_SIZE = 6;
const HOT_KEYWORDS = ["出轨", "婚姻", "家庭伦理", "悬疑", "女性成长", "影评"];

type Tab = "all" | "drama" | "review";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<Tab>("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo({
    title: query ? `“${query}”的搜索结果 - 出轨的妻子影视大全` : "搜索 - 出轨的妻子影视大全",
    description:
      "在出轨的妻子影视大全中搜索影视作品与影评文章，支持按标题、题材与标签模糊匹配，快速找到你想了解的剧情与解读。",
    keywords: "搜索,出轨的妻子,影视搜索,影评搜索,剧情查询",
  });

  const matchedDramas = useMemo(() => {
    if (!query) return [];
    const q = query.trim().toLowerCase();
    return dramas.filter((d) =>
      [d.title, d.genre, d.summary, ...d.tags, ...d.cast].join(" ").toLowerCase().includes(q)
    );
  }, [query]);

  const matchedReviews = useMemo(() => {
    if (!query) return [];
    const q = query.trim().toLowerCase();
    return reviews.filter((r) =>
      [r.title, r.excerpt, r.author, ...r.tags].join(" ").toLowerCase().includes(q)
    );
  }, [query]);

  const total = matchedDramas.length + matchedReviews.length;
  const showDramas = tab === "all" || tab === "drama";
  const showReviews = tab === "all" || tab === "review";

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = input.trim();
    setVisible(PAGE_SIZE);
    setSearchParams(q ? { q } : {});
  };

  const runKeyword = (kw: string) => {
    setInput(kw);
    setVisible(PAGE_SIZE);
    setSearchParams({ q: kw });
  };

  const dramaShown = matchedDramas.slice(0, visible);
  const reviewShown = matchedReviews.slice(0, visible);
  const hasMore =
    (showDramas && matchedDramas.length > visible) || (showReviews && matchedReviews.length > visible);

  const recommended = useMemo(
    () => [...dramas].sort((a, b) => b.popularity - a.popularity).slice(0, 3),
    []
  );

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "all", label: "全部", count: total },
    { key: "drama", label: "影视作品", count: matchedDramas.length },
    { key: "review", label: "影评文章", count: matchedReviews.length },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-6 mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">站内搜索</h1>
        <p className="mt-3 text-sm md:text-base leading-7 text-foreground-600 max-w-3xl">
          输入关键词，即可在影视作品标题、题材、标签与影评文章中模糊匹配，快速定位你想了解的内容。
        </p>
      </header>

      {/* Search form */}
      <form
        onSubmit={submit}
        className="flex flex-col sm:flex-row gap-3 max-w-2xl"
      >
        <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5">
          <i className="ri-search-line text-foreground-500" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索影视作品或影评，例如：出轨、婚姻、悬疑"
            className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
          {input && (
            <button
              type="button"
              aria-label="清空"
              onClick={() => setInput("")}
              className="w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      {!query ? (
        <div className="mt-10">
          <p className="text-xs font-medium text-foreground-500 mb-3">热门搜索</p>
          <div className="flex flex-wrap gap-2">
            {HOT_KEYWORDS.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => runKeyword(kw)}
                className="rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {kw}
              </button>
            ))}
          </div>

          <div className="mt-12">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">为你推荐的热门影视</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
              {recommended.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <p className="text-sm text-foreground-600">
              关键词 <span className="font-medium text-foreground-950">“{query}”</span> 共找到{" "}
              <span className="font-heading text-lg font-bold text-accent-600">{total}</span> 条结果
            </p>
            <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-1 py-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    setTab(t.key);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                    tab === t.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {t.label}（{t.count}）
                </button>
              ))}
            </div>
          </div>

          {total === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title={`没有找到与“${query}”相关的内容`}
              description="换个关键词试试，或者看看下面为你推荐的热门影视作品。"
            />
          ) : (
            <div className="space-y-12">
              {showDramas && matchedDramas.length > 0 && (
                <section>
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2.5">
                    <span className="w-1 h-5 rounded-full bg-accent-500" />
                    影视作品（{matchedDramas.length}）
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                    {dramaShown.map((d) => (
                      <DramaCard key={d.id} drama={d} />
                    ))}
                  </div>
                </section>
              )}

              {showReviews && matchedReviews.length > 0 && (
                <section>
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2.5">
                    <span className="w-1 h-5 rounded-full bg-accent-500" />
                    影评文章（{matchedReviews.length}）
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                    {reviewShown.map((r) => (
                      <ReviewCard key={r.id} review={r} />
                    ))}
                  </div>
                </section>
              )}

              {hasMore && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line" />
                    加载更多
                  </button>
                </div>
              )}
            </div>
          )}

          {total === 0 && (
            <div className="mt-12">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">你可能感兴趣</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                {recommended.map((d) => (
                  <DramaCard key={d.id} drama={d} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              或前往影视分类页浏览全部作品
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}